/**
 * landingVisit — RPC contract for the callable revia.to's landing calls once
 * per arrival, counting the visit in that day's
 * `workspaces/revia/landingViews/{yyyy-mm-dd}` (`landing-views.entity.ts`).
 *
 * Cross-process contract between the landing (caller, not signed in) and
 * Revia's server `landingVisit` handler (onCall). Both ends parse with
 * `.strict()` — an extra key fails the whole request.
 *
 * Nothing identifies the visitor: no sign-in, no uid, nothing stored on the
 * device, no record per visitor. The server adds one to the day's counters
 * for the path, the referring origin and the campaign source. App Check is
 * off: enforcing it loads reCAPTCHA on every landing view, which the
 * landing's no-third-party-script rule does not admit.
 *
 * Abuse bound: about 60 counted visits per hour per network address. The
 * server keys its limiter on an HMAC of the caller's address under a salt
 * derived each UTC day from the platform key and never stored, truncated, so
 * the stored limiter key names no address and no visitor across days.
 *
 * Fields justified:
 *   path     — the arrival's pathname; the landing has one surface, `/`, so
 *              the contract admits exactly that path.
 *   referrer — the origin that sent the visitor, never its path; absent on a
 *              direct arrival or a move inside the site.
 *   utm      — the arrival's campaign parameters, each nullish because a
 *              campaign link may carry any subset. `source` is counted;
 *              `medium` and `campaign` are accepted as the landing reads them
 *              and not counted yet.
 *
 * Output: empty. A counted visit and one past the cap look alike.
 *
 * Excluded:
 *   uid / client / user agent — the counts need no visitor identity.
 *   utm_term / utm_content — no reader of a landing visit.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   invalid-argument   — the input does not parse.
 *   resource-exhausted — the caller's address has used its hour's share;
 *                        nothing is written.
 */
import { z } from "zod";
import {
	LandingPathSchema,
	ReferrerOriginSchema,
	UtmValueSchema,
} from "./landing-views.entity.ts";

export const LandingVisitInput = z
	.object({
		path: LandingPathSchema,
		referrer: ReferrerOriginSchema.nullish(),
		utm: z
			.object({
				source: UtmValueSchema.nullish(),
				medium: UtmValueSchema.nullish(),
				campaign: UtmValueSchema.nullish(),
			})
			.strict()
			.nullish()
			.describe("the campaign parameters the arrival URL carried"),
	})
	.strict();
export type LandingVisitInput = z.infer<typeof LandingVisitInput>;

export const LandingVisitOutput = z.object({}).strict();
export type LandingVisitOutput = z.infer<typeof LandingVisitOutput>;
